import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import './EftTable.css';
import PayoutTableSkeleton from '../../../SkeletonEffect/PayoutTableSkeleton/PayoutTableSkeleton';

// pending has no bank reference yet; failed is a retry away, not an end state.
const STATUS_STYLE = {
  pending: { color: '#E0AD14', bgcolor: '#FFFCF2', label: 'Pending' },
  transferred: { color: '#14B334', bgcolor: '#F2FAF3', label: 'Transferred' },
  failed: { color: '#E14949', bgcolor: '#FFF2F2', label: 'Failed' },
};

export function TransferredTable({ data, isLoading = false, onView, onEdit, onDelete }) {
  // One anchor for the whole table: only one kebab menu is ever open.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const openMenu = (event, row) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const runAction = handler => () => {
    const row = menuRow;
    closeMenu();
    if (handler && row) handler(row);
  };

  if (isLoading) {
    return <PayoutTableSkeleton />;
  }

  const rows = data || [];
  // A missing handler means the admin lacks that permission: hide the action,
  // and the kebab altogether when nothing is left in it.
  const hasMenuActions = Boolean(onEdit || onDelete);

  return (
    <Box className="eft-table-container">
      <table className="eft-table">
        <thead>
          <tr>
            <th className="eft-th">EFT ID</th>
            <th className="eft-th">Payment Count</th>
            <th className="eft-th">Total Amount (QAR)</th>
            <th className="eft-th">Earliest Payment Date</th>
            <th className="eft-th">Latest Payment Date</th>
            <th className="eft-th">Transaction ID</th>
            <th className="eft-th">Transaction Date</th>
            <th className="eft-th">Claimed On</th>
            <th className="eft-th">Status</th>
            <th className="eft-th">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={10} className="eft-empty-cell">
                <Box className="eft-empty-state">
                  <Typography className="eft-empty-text">
                    No transfers for the selected filters
                  </Typography>
                </Box>
              </td>
            </tr>
          ) : (
            rows.map(row => {
              const style = STATUS_STYLE[row.status] || STATUS_STYLE.pending;
              return (
                <tr key={row.id} className="eft-row">
                  <td className="eft-td">{row.referenceNo}</td>
                  <td className="eft-td eft-count-cell">{row.itemCount}</td>
                  <td className="eft-td">{row.totalAmount}</td>
                  <td className="eft-td">{row.earliestPaymentDate}</td>
                  <td className="eft-td">{row.latestPaymentDate}</td>
                  <td className={`eft-td ${row.bankReference ? '' : 'eft-muted-cell'}`}>
                    {row.bankReference ? (
                      // Up to 100 chars; clipped here, in full on hover and on the detail page.
                      <Tooltip title={row.bankReference} placement="top" arrow>
                        <span className="eft-reference-cell">{row.bankReference}</span>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className={`eft-td ${row.transferredAt ? '' : 'eft-muted-cell'}`}>
                    {row.transferredAt || '-'}
                  </td>
                  <td className="eft-td">{row.createdAt}</td>
                  <td className="eft-td">
                    <Box className="eft-status-container">
                      <Box
                        className="eft-status-badge"
                        sx={{
                          border: `1px solid ${style.color}`,
                          backgroundColor: style.bgcolor,
                          color: style.color,
                        }}
                      >
                        <Box className="eft-status-dot" sx={{ backgroundColor: style.color }} />
                        <Typography className="eft-status-text">{style.label}</Typography>
                      </Box>
                    </Box>
                  </td>
                  <td className="eft-td">
                    <Box className="eft-action-cell">
                      <button
                        type="button"
                        className="eft-view-link"
                        onClick={() => onView && onView(row)}
                      >
                        View
                      </button>
                      {hasMenuActions && (
                        <IconButton
                          size="small"
                          aria-label={`Actions for ${row.referenceNo}`}
                          onClick={event => openMenu(event, row)}
                        >
                          <MoreVertIcon sx={{ fontSize: 18, color: '#0a5ef3' }} />
                        </IconButton>
                      )}
                    </Box>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {onEdit && (
          <MenuItem className="eft-menu-item" onClick={runAction(onEdit)}>
            {menuRow?.bankReference ? 'Edit transaction details' : 'Add transaction details'}
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem className="eft-menu-item eft-menu-item-danger" onClick={runAction(onDelete)}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
